import { For, Show, createMemo, createSignal, untrack, type JSX } from 'solid-js';
import { ChevronLeft, ChevronRight } from 'lucide-solid';
import { cn } from '~/shared/lib/cn';
import { addDays } from '~/shared/lib/clubClock';
import { monthRange, toLocalIsoDate } from '~/shared/lib/calendarDays';
import { useClubClock } from '~/shared/lib/useClubClock';

const MONTHS_FULL = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
const MONTHS_SHORT = [
  'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
  'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек',
];
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

type NavLevel = 'days' | 'months' | 'years';

interface CalendarProps {
  value?: string;
  rangeStart?: string;
  rangeEnd?: string;
  rangeHover?: string;
  mode?: 'single' | 'range';
  min?: string;
  max?: string;
  onPick: (iso: string) => void;
  onHover?: (iso: string | null) => void;
  defaultMonth?: string;
  footer?: JSX.Element;
}

const firstOfMonth = (iso: string): string => `${iso.slice(0, 7)}-01`;

export function Calendar(props: CalendarProps): JSX.Element {
  const clock = useClubClock();
  const today = () => clock.today();
  const seed = props.value || props.rangeStart || props.defaultMonth || untrack(today);

  const [view, setView] = createSignal(firstOfMonth(seed));
  const [level, setLevel] = createSignal<NavLevel>('days');
  const [focused, setFocused] = createSignal(props.value || props.rangeStart || untrack(today));

  const dayRefs: Record<string, HTMLButtonElement | undefined> = {};

  const year = () => Number(view().slice(0, 4));
  const month = () => Number(view().slice(5, 7)) - 1;

  const disabled = (iso: string): boolean =>
    (props.min !== undefined && iso < props.min) ||
    (props.max !== undefined && iso > props.max);

  const rangeBounds = createMemo<{ lo: string; hi: string } | null>(() => {
    if (props.mode !== 'range' || !props.rangeStart) return null;
    const end = props.rangeEnd ?? props.rangeHover;
    if (!end) return null;
    return props.rangeStart <= end
      ? { lo: props.rangeStart, hi: end }
      : { lo: end, hi: props.rangeStart };
  });

  const isSelected = (iso: string): boolean =>
    props.mode === 'range'
      ? iso === props.rangeStart || iso === props.rangeEnd
      : iso === props.value;

  const inBand = (iso: string): boolean => {
    const b = rangeBounds();
    return !!b && iso > b.lo && iso < b.hi;
  };

  const moveFocus = (delta: number) => {
    const next = addDays(focused(), delta);
    setFocused(next);
    if (next.slice(0, 7) !== view().slice(0, 7)) setView(firstOfMonth(next));
    queueMicrotask(() => dayRefs[next]?.focus());
  };

  const onGridKey = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowLeft': e.preventDefault(); moveFocus(-1); break;
      case 'ArrowRight': e.preventDefault(); moveFocus(1); break;
      case 'ArrowUp': e.preventDefault(); moveFocus(-7); break;
      case 'ArrowDown': e.preventDefault(); moveFocus(7); break;
      case 'Home': e.preventDefault(); moveFocus(-((new Date(`${focused()}T00:00:00`).getDay() + 6) % 7)); break;
      case 'End': e.preventDefault(); moveFocus(6 - ((new Date(`${focused()}T00:00:00`).getDay() + 6) % 7)); break;
      case 'PageUp': e.preventDefault(); stepMonth(-1); break;
      case 'PageDown': e.preventDefault(); stepMonth(1); break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!disabled(focused())) props.onPick(focused());
        break;
    }
  };

  const stepMonth = (dir: -1 | 1) => {
    const d = new Date(year(), month() + dir, 1);
    setView(toLocalIsoDate(d));
  };
  const stepHeader = (dir: -1 | 1) => {
    if (level() === 'days') return stepMonth(dir);
    if (level() === 'months') return setView(toLocalIsoDate(new Date(year() + dir, month(), 1)));
    setView(toLocalIsoDate(new Date(year() + dir * 12, month(), 1)));
  };

  const yearBase = createMemo(() => Math.floor(year() / 12) * 12);

  const headerTitle = (): string => {
    if (level() === 'days') return `${MONTHS_FULL[month()]} ${year()}`;
    if (level() === 'months') return String(year());
    return `${yearBase()} – ${yearBase() + 11}`;
  };
  const escalate = () => setLevel((l) => (l === 'days' ? 'months' : 'years'));

  const cellBase =
    'press relative grid place-items-center rounded-md text-[13px] tabular transition-colors duration-fast';

  return (
    <div
      class="glass-menu p-3 select-none animate-pop-in"
      style={{
        width: '266px',
      }}
    >
      <div class="flex items-center gap-1 mb-2">
        <button
          type="button"
          class="press h-8 w-8 rounded-md hover:bg-white/8 grid place-items-center text-secondary"
          onClick={() => stepHeader(-1)}
          aria-label="Назад"
        >
          <ChevronLeft class="h-4 w-4" />
        </button>
        <button
          type="button"
          class="press flex-1 h-8 rounded-md hover:bg-white/8 text-[13px] font-semibold text-primary tabular"
          onClick={escalate}
          aria-label="Сменить режим выбора"
        >
          {headerTitle()}
        </button>
        <button
          type="button"
          class="press h-8 w-8 rounded-md hover:bg-white/8 grid place-items-center text-secondary"
          onClick={() => stepHeader(1)}
          aria-label="Вперёд"
        >
          <ChevronRight class="h-4 w-4" />
        </button>
      </div>

      <Show when={level() === 'days'}>
        <div class="grid grid-cols-7 mb-1">
          <For each={WEEKDAYS}>
            {(w, i) => (
              <div
                class="h-7 grid place-items-center text-[10.5px] font-medium uppercase tracking-[0.04em] text-tertiary"
                style={i() >= 5 ? { color: 'color-mix(in srgb, var(--color-warning) 60%, var(--color-text-tertiary))' } : {}}
              >
                {w}
              </div>
            )}
          </For>
        </div>
        <div class="grid grid-cols-7 gap-y-0.5" onKeyDown={onGridKey} role="grid">
          <For each={monthRange(view()).days}>
            {(iso) => {
              const inMonth = () => iso.slice(0, 7) === view().slice(0, 7);
              const sel = () => isSelected(iso);
              const band = () => inBand(iso);
              const isToday = () => iso === today();
              const off = () => disabled(iso);
              return (
                <div
                  class="relative grid place-items-center"
                  style={{
                    background: band()
                      ? 'color-mix(in srgb, var(--color-accent) 14%, transparent)'
                      : 'transparent',
                  }}
                >
                  <button
                    type="button"
                    ref={(el) => (dayRefs[iso] = el)}
                    tabindex={iso === focused() ? 0 : -1}
                    disabled={off()}
                    class={cn(
                      cellBase,
                      'h-9 w-9',
                      sel()
                        ? 'text-white font-semibold'
                        : off()
                          ? 'text-disabled cursor-not-allowed'
                          : inMonth()
                            ? 'text-primary hover:bg-white/8'
                            : 'text-disabled hover:bg-white/5',
                    )}
                    style={
                      sel()
                        ? { background: 'var(--color-accent)' }
                        : isToday()
                          ? { 'box-shadow': 'inset 0 0 0 1px var(--color-accent)', color: 'var(--color-accent)' }
                          : {}
                    }
                    onClick={() => {
                      setFocused(iso);
                      props.onPick(iso);
                    }}
                    onMouseEnter={() => props.onHover?.(iso)}
                    onFocus={() => setFocused(iso)}
                  >
                    {Number(iso.slice(8, 10))}
                  </button>
                </div>
              );
            }}
          </For>
        </div>
      </Show>

      <Show when={level() === 'months'}>
        <div class="grid grid-cols-3 gap-1.5 py-1">
          <For each={MONTHS_SHORT}>
            {(m, i) => {
              const active = () =>
                i() === month() &&
                (props.value?.slice(0, 4) === String(year()) ||
                  props.rangeStart?.slice(0, 4) === String(year()));
              return (
                <button
                  type="button"
                  class={cn(
                    'press h-11 rounded-md text-[13px] font-medium transition-colors',
                    active() ? 'text-white' : 'text-secondary hover:bg-white/8',
                  )}
                  style={active() ? { background: 'var(--color-accent)' } : {}}
                  onClick={() => {
                    setView(toLocalIsoDate(new Date(year(), i(), 1)));
                    setLevel('days');
                  }}
                >
                  {m}
                </button>
              );
            }}
          </For>
        </div>
      </Show>

      <Show when={level() === 'years'}>
        <div class="grid grid-cols-3 gap-1.5 py-1">
          <For each={Array.from({ length: 12 }, (_, i) => yearBase() + i)}>
            {(y) => {
              const active = () => y === year();
              return (
                <button
                  type="button"
                  class={cn(
                    'press h-11 rounded-md text-[13px] font-medium tabular transition-colors',
                    active() ? 'text-white' : 'text-secondary hover:bg-white/8',
                  )}
                  style={active() ? { background: 'var(--color-accent)' } : {}}
                  onClick={() => {
                    setView(toLocalIsoDate(new Date(y, month(), 1)));
                    setLevel('months');
                  }}
                >
                  {y}
                </button>
              );
            }}
          </For>
        </div>
      </Show>

      <Show when={props.footer}>
        <div class="mt-2 pt-2 border-t-hairline flex items-center gap-1.5">{props.footer}</div>
      </Show>
    </div>
  );
}
